import {
  Box,
  Button,
  Flex,
  Heading,
  Input,
  Stack,
  Text,
  Image,
} from "@chakra-ui/react";
import { SubmitHandler, useForm } from "react-hook-form";
import EmailDomainSuggestion from "../components/EmailDomainSuggestion";
import { useEffect, useState } from "react";
import loginBg from "../assets/images/login-bg.webp";
import AlertCircleIcon from "../components/AlertCircleIcon";
import AfterSendingLinkSection from "../components/AfterSendingLinkSection";
import * as AuthAPI from "../apis/auth";

type EmailInputs = {
  email: string;
};

const LoginPage = () => {
  const [showEmailSuggestions, setShowEmailSuggestions] = useState(false);
  const [isSendEmail, setIsSendEmail] = useState(false);
  const [isInvalid, setIsInvalid] = useState(false);
  const [isRequesting, setIsRequesting] = useState(false);
  const [isFocusing, setIsFocusing] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
  } = useForm<EmailInputs>();

  const email = watch("email");

  useEffect(() => {
    if (email && !email.includes("@")) {
      setShowEmailSuggestions(true);
    } else {
      setShowEmailSuggestions(false);
    }
  }, [email]);

  const onSubmit: SubmitHandler<EmailInputs> = async ({ email }) => {
    try {
      setIsRequesting(true);
      await AuthAPI.login(email);

      setIsSendEmail(true);
    } catch (error) {
      console.log("error", error);
      setIsInvalid(true);
    } finally {
      setIsRequesting(false);
    }
  };

  const onEmailSuggestionClick = (email: string) => {
    setValue("email", email);
    setShowEmailSuggestions(false);
  };

  return (
    <>
      <Flex w="100vw" h="100vh" alignItems="center" justifyContent="center">
        <Box
          width={{
            base: "90%",
            md: "max-content",
          }}
          margin="0 auto"
        >
          {isSendEmail ? (
            <AfterSendingLinkSection userEmail={email} />
          ) : (
            <Flex
              as="form"
              p={{ base: "40px 15px", md: "40px 36px" }}
              w={{ base: "100%", md: "508px" }}
              h={{ base: "300px", md: "auto" }}
              margin="0 auto"
              bg="#fff"
              border="1px solid #EAECF0"
              borderRadius="15px"
              flexDirection="column"
              boxShadow="0px 24px 48px 0px #0000000A"
              onSubmit={handleSubmit(onSubmit)}
            >
              <Heading mb="28px" display="flex" justifyContent="center">
                <img
                  src="/images/viral-logo.webp"
                  alt="viral-logo"
                  height="18"
                  width="107"
                />
              </Heading>
              <Heading
                fontSize={{ base: "16px", md: "22px" }}
                fontWeight="400"
                lineHeight={{
                  base: "19.47px",
                  md: "26.77px",
                }}
                p="0 16.5px"
                mb={{ base: "32px", md: "20px" }}
                color="#302E2E"
                textAlign="center"
              >
                <Text
                  fontSize="20px"
                  fontWeight="700"
                  lineHeight="25.3px"
                  textAlign="center"
                  color="#000000"
                >
                  Enter your purchase email <br className="md:hidden" /> to log
                  in
                </Text>
              </Heading>

              <Stack
                spacing="15px"
                mb={{ base: "17px", md: "30px" }}
                position="relative"
              >
                <Box>
                  <Input
                    placeholder="Email Address"
                    type="email"
                    borderRadius="12px"
                    height={{ base: "38px", md: "42px" }}
                    fontSize="14px"
                    border="1px solid #EAECF0"
                    color="#424242"
                    _placeholder={{ color: "#888888", fontWeight: "400" }}
                    _focus={{ borderColor: "primary" }}
                    _focusVisible={{ boxShadow: "0 0 0 2px #FECDCA" }}
                    {...register("email", { required: "Email is required" })}
                    borderColor={isInvalid || email ? "primary" : ""}
                    onFocus={() => setIsFocusing(true)}
                    onBlur={() => setIsFocusing(false)}
                  />

                  {email && isInvalid && (
                    <Flex
                      position="absolute"
                      top="50%"
                      right="12px"
                      transform="translateY(-50%)"
                      align="center"
                    >
                      <AlertCircleIcon />
                    </Flex>
                  )}

                  {errors.email && (
                    <Text color="red.500" fontSize="14px" mt="3px">
                      {errors.email.message}
                    </Text>
                  )}
                </Box>

                {showEmailSuggestions && (
                  <EmailDomainSuggestion
                    email={email}
                    onEmailSuggestionClick={onEmailSuggestionClick}
                  />
                )}
              </Stack>

              <Button
                p="8px 16px"
                h={{ base: "40px", md: "56px" }}
                textAlign="center"
                borderRadius="12px"
                background={isFocusing ? "primary" : "#EAECF0"}
                color={isFocusing ? "#fff" : "#98A2B3"}
                _hover={{ background: "primary", color: "#fff" }}
                _active={{ background: "primary", color: "#fff" }}
                type="submit"
                isLoading={isRequesting}
                loadingText="Log In"
              >
                <Text
                  fontWeight="700"
                  fontSize="16px"
                  lineHeight="18px"
                  mr="10px"
                >
                  Log In
                </Text>
              </Button>
            </Flex>
          )}

          {isInvalid && !isSendEmail && (
            <Flex
              mt="20px"
              borderTop="1px solid #eaecf0"
              cursor="pointer"
              w={{ base: "90%", md: "508px" }}
              height="50px"
              bg="#fff"
              border="1px solid #EAECF0"
              borderRadius="15px"
              flexDirection="column"
              background="#FFFFFF"
              position="absolute"
            >
              <div className="flex h-full items-center">
                <Box
                  background="#FFE9E8"
                  w="26px"
                  h="26px"
                  mx="16px"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  borderRadius="9px"
                >
                  <AlertCircleIcon />
                </Box>

                <Text fontWeight="450" fontSize="14px" lineHeight="17.1px">
                  Purchase Email Address Invalid
                </Text>
              </div>
            </Flex>
          )}
        </Box>
        <Image
          src={loginBg}
          alt="Opt In"
          position="absolute"
          bottom="0"
          left="0"
          zIndex="-1"
          w="100%"
          h="100%"
        />
      </Flex>
    </>
  );
};

export default LoginPage;
